import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperAirplaneIcon, ArrowPathIcon, ChatBubbleBottomCenterTextIcon, BookOpenIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BrainIcon } from './icons/BrainIcon';
import { SearchIcon } from './icons/SearchIcon';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import ChatMessage from './ChatMessage';
import SearchingIndicator from './SearchingIndicator';
import SourceCitations from './SourceCitations';
import OpenverseRecommendations from './OpenverseRecommendations';
import ChatHistory from './ChatHistory';
import GandalfLogo from '../common/GandalfLogo';
import { Wand2 } from 'lucide-react';
import { Brain } from 'lucide-react';

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const [sources, setSources] = useState([]);
  const [openverseRecommendations, setOpenverseRecommendations] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chatHistoryRefreshTrigger, setChatHistoryRefreshTrigger] = useState(0);
  const [chatMode, setChatMode] = useState(() => {
    // Load mode from localStorage, default to 'search'
    return localStorage.getItem('gandalf_chat_mode') || 'search';
  }); // 'search' or 'coach'
  
  // Mobile panel states
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [isSourcesPanelOpen, setIsSourcesPanelOpen] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sourceRefs = useRef({});
  const { user } = useAuth();

  // Auto-resize textarea function
  const adjustTextareaHeight = () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate new height based on content, with min and max limits
    const minHeight = 48; // 3rem equivalent 
    const maxHeight = 200; // Maximum height before scrolling
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    
    textarea.style.height = `${newHeight}px`;
  };

  // Handle input changes with auto-resize
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    // Adjust height after state update
    requestAnimationFrame(adjustTextareaHeight);
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Adjust textarea height on mount and when value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle mode change with persistence
  const handleModeChange = (newMode) => {
    setChatMode(newMode);
    localStorage.setItem('gandalf_chat_mode', newMode);
  };

  // Handle prompt click with potential mode switching
  const handlePromptClick = (question) => {
    // Check if this is a cross-mode prompt and switch modes accordingly
    if (question.includes('(try Coach Mode)')) {
      handleModeChange('coach');
      setInputValue(question.replace(' (try Coach Mode)', ''));
    } else if (question.includes('(try Search Mode)')) {
      handleModeChange('search');
      setInputValue(question.replace(' (try Search Mode)', ''));
    } else {
      setInputValue(question);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamingMessage]);

  const loadChatThread = async (conversationId) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      // Load the conversation thread by ID
      const { data, error } = await supabase
        .from('chat_history')
        .select('*')
        .eq('user_id', user.id)
        .eq('id', conversationId)
        .single();
      
      if (error) throw error;
      
      // Convert conversation thread back to message format
      const threadMessages = [];
      
      if (data) {
        // Check if this is a conversation thread (answer is JSON array) or single Q&A
        if (data.answer && data.answer.startsWith('[')) {
          try {
            const conversationHistory = JSON.parse(data.answer);
            
            conversationHistory.forEach((qa, index) => {
              // Add user message
              threadMessages.push({
                id: `user-${data.id}-${index}`,
                content: qa.question,
                role: 'user',
                timestamp: new Date(qa.timestamp || data.created_at)
              });
              
              // Add assistant message
              if (qa.answer) {
                threadMessages.push({
                  id: `assistant-${data.id}-${index}`,
                  content: qa.answer,
                  role: 'assistant',
                  timestamp: new Date(qa.timestamp || data.created_at),
                  sources: qa.sources || []
                });
              }
            });
            
            // Set sources and Openverse recommendations from the last message
            const lastMessage = conversationHistory[conversationHistory.length - 1];
            setSources(lastMessage?.sources || []);
            setOpenverseRecommendations(lastMessage?.openverse_recommendations || []);
            
          } catch (parseError) {
            console.error('Error parsing conversation thread:', parseError);
            // Fallback to single Q&A format
            threadMessages.push({
              id: `user-${data.id}`,
              content: data.question,
              role: 'user',
              timestamp: new Date(data.created_at)
            });
            
            if (data.answer) {
              threadMessages.push({
                id: `assistant-${data.id}`,
                content: data.answer,
                role: 'assistant',
                timestamp: new Date(data.created_at),
                sources: data.sources || []
              });
            }
            setSources(data.sources || []);
            setOpenverseRecommendations([]);
          }
        } else {
          // Single Q&A format (legacy)
          threadMessages.push({
            id: `user-${data.id}`,
            content: data.question,
            role: 'user',
            timestamp: new Date(data.created_at)
          });
          
          if (data.answer) {
            threadMessages.push({
              id: `assistant-${data.id}`,
              content: data.answer,
              role: 'assistant',
              timestamp: new Date(data.created_at),
              sources: data.sources || []
            });
          }
          setSources(data.sources || []);
          setOpenverseRecommendations([]); // Legacy single Q&A format won't have openverse recommendations
        }
      }
      
      // Sort by timestamp
      threadMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      setMessages(threadMessages);
      setCurrentChatId(conversationId);
      
    } catch (error) {
      console.error('Error loading chat thread:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = async () => {
    if (!user) {
      // For anonymous users, just clear the UI
      setMessages([]);
      setCurrentChatId(null);
      setSources([]);
      setOpenverseRecommendations([]);
      return;
    }

    try {
      
      // Generate a proper UUID for session_id
      const sessionId = crypto.randomUUID();
      
      // Create a placeholder "New conversation" entry in the database
      const placeholderData = {
        user_id: user.id,
        session_id: sessionId,
        question: "New conversation",
        answer: null, // No answer yet
        sources: [],
        search_metadata: {
          is_placeholder: true,
          created_at: new Date().toISOString()
        }
      };

      const { data, error } = await supabase
        .from('chat_history')
        .insert(placeholderData)
        .select()
        .single();

      if (error) {
        console.error('ChatInterface: Error creating new conversation placeholder:', error);
        // Fall back to just clearing the UI
        setMessages([]);
        setCurrentChatId(null);
        setSources([]);
        setOpenverseRecommendations([]);
        return;
      }
      
      // Clear the UI and set the new conversation as current
      setMessages([]);
      setCurrentChatId(data.id);
      setSources([]);
      setOpenverseRecommendations([]);

      // Trigger chat history refresh to show the new placeholder
      setChatHistoryRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('ChatInterface: Error in startNewChat:', error);
      // Fall back to just clearing the UI
      setMessages([]);
      setCurrentChatId(null);
      setSources([]);
      setOpenverseRecommendations([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    // If no active conversation and user is authenticated, create placeholder first
    if (!currentChatId && user) {
      
      try {
        // Generate a proper UUID for session_id
        const sessionId = crypto.randomUUID();
        
        // Create a placeholder "New conversation" entry in the database
        const placeholderData = {
          user_id: user.id,
          session_id: sessionId,
          question: "New conversation",
          answer: null, // No answer yet
          sources: [],
          search_metadata: {
            is_placeholder: true,
            created_at: new Date().toISOString()
          }
        };

        const { data, error } = await supabase
          .from('chat_history')
          .insert(placeholderData)
          .select()
          .single();

        if (error) {
          console.error('ChatInterface: Error creating placeholder before message:', error);
          // Continue without placeholder - don't block the user
        } else {
          setCurrentChatId(data.id);
          // Trigger chat history refresh to show the new placeholder
          setChatHistoryRefreshTrigger(prev => prev + 1);
        }
      } catch (error) {
        console.error('ChatInterface: Error in placeholder creation:', error);
        // Continue without placeholder - don't block the user
      }
    }

    const userMessage = {
      id: Date.now(),
      content: inputValue,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsSearching(true);
    setCurrentStreamingMessage('');
    setSources([]);
    setOpenverseRecommendations([]);

    try {
      // Get the session token for authorization
      let authToken = '';
      if (user) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          authToken = session?.access_token || '';
        } catch (error) {
          console.error('ChatInterface: Error getting session token:', error);
        }
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        },
        body: JSON.stringify({
          message: inputValue,
          conversation_history: messages.slice(-10), // Last 10 messages for context
          mode: chatMode // Pass the current chat mode to the API
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let sourcesReceived = [];
      let openverseReceived = [];
      let buffer = '';
      let hasReceivedFirstContent = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');
        
        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataContent = line.slice(6);
            
            // Skip the [DONE] signal
            if (dataContent === '[DONE]') {
              continue;
            }
            
            try {
              const data = JSON.parse(dataContent);
              
              if (data.type === 'content') {
                // Hide searching indicator on first content chunk (no gap!)
                if (!hasReceivedFirstContent) {
                  setIsSearching(false);
                  hasReceivedFirstContent = true;
                }
                assistantMessage += data.content;
                setCurrentStreamingMessage(assistantMessage);
              } else if (data.type === 'sources') {
                sourcesReceived = data.sources;
                setSources(sourcesReceived);
                // Also hide searching indicator when sources arrive (backup in case no content comes)
                if (!hasReceivedFirstContent) {
                  setIsSearching(false);
                  hasReceivedFirstContent = true;
                }
              } else if (data.type === 'openverse_recommendations') {
                openverseReceived = data.recommendations;
                setOpenverseRecommendations(openverseReceived);
              }
            } catch (error) {
              console.error('Error parsing streaming data:', error, 'Line:', line);
              // Continue processing other lines instead of breaking
            }
          }
        }
      }

      // Add the complete assistant message
      const assistantMessageObj = {
        id: Date.now() + 1,
        content: assistantMessage,
        role: 'assistant',
        timestamp: new Date(),
        sources: sourcesReceived
      };

      setMessages(prev => [...prev, assistantMessageObj]);
      setCurrentStreamingMessage('');
      setSources(sourcesReceived);
      setOpenverseRecommendations(openverseReceived);

      // Trigger chat history refresh to show updated conversation
      setChatHistoryRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error sending message:', error);
      setIsSearching(false); // Ensure searching indicator is hidden on error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden lg:ml-16">
      {/* Branded Background */}
      <div className="absolute inset-0 opacity-10">
        {/* Subtle geometric pattern */}
        <div className="absolute top-0 left-0 w-full h-full">
          <svg className="w-full h-full" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Organizational network pattern */}
            <defs>
              <pattern id="org-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <circle cx="30" cy="30" r="1" fill="currentColor" opacity="0.3"/>
                <circle cx="0" cy="0" r="0.5" fill="currentColor" opacity="0.2"/>
                <circle cx="60" cy="0" r="0.5" fill="currentColor" opacity="0.2"/>
                <circle cx="0" cy="60" r="0.5" fill="currentColor" opacity="0.2"/>
                <circle cx="60" cy="60" r="0.5" fill="currentColor" opacity="0.2"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#org-grid)" className="text-indigo-400"/>
            
            {/* Flowing connection lines */}
            <path d="M100,200 Q300,150 500,200 T900,180" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3" className="text-purple-400"/>
            <path d="M200,400 Q400,350 600,400 T1000,380" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.25" className="text-indigo-400"/>
            <path d="M50,600 Q350,550 650,600 T1150,580" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.2" className="text-purple-300"/>
            
            {/* Key organizational nodes */}
            <circle cx="300" cy="200" r="3" fill="currentColor" opacity="0.4" className="text-indigo-400"/>
            <circle cx="600" cy="380" r="2" fill="currentColor" opacity="0.3" className="text-purple-400"/>
            <circle cx="900" cy="180" r="2.5" fill="currentColor" opacity="0.35" className="text-indigo-300"/>
          </svg>
        </div>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 bg-gray-800/50 border-b border-gray-700/50 backdrop-blur-sm mt-4 pt-2"
      >
        <div className="max-w-none mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Mobile menu button */}
            <div className="flex items-center space-x-4">
              {user && (
                <button
                  onClick={() => setIsHistoryPanelOpen(true)}
                  className="lg:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700/50 transition-colors"
                  aria-label="Open thread history"
                >
                                      <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                </button>
              )}
              
              {/* Gandalf Info */}
              <div className="flex items-center space-x-3">
                <motion.div
                  className="relative w-12 h-12 bg-gradient-to-br from-purple-400 to-indigo-600 rounded-full flex items-center justify-center shadow-lg p-3"
                  whileHover={{ scale: 1.05 }}
                  animate={{ 
                    boxShadow: [
                      "0 0 20px rgba(139, 92, 246, 0.3)",
                      "0 0 30px rgba(139, 92, 246, 0.5)",
                      "0 0 20px rgba(139, 92, 246, 0.3)"
                    ]
                  }}
                  transition={{ 
                    boxShadow: { 
                      duration: 2, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }
                  }}
                >
                  <GandalfLogo size="large" className="text-white" />
                </motion.div>
                <div className="hidden sm:block">
                  <div className="flex items-center space-x-3">
                    <h1 className="text-xl sm:text-2xl font-bold text-white">Gandalf</h1>
                  </div>
                  <div className="text-gray-400 text-sm hidden md:flex items-center gap-2">
                    <span>Your Open Org wizard •</span>
                    <div className="flex items-center gap-1">
                      <SearchIcon className="w-3 h-3 text-primary-light" />
                      <span className="text-primary-light">Search mode</span>
                    </div>
                    <span>or</span>
                    <div className="flex items-center gap-1">
                      <BrainIcon className="w-3 h-3 text-green-400" />
                      <span className="text-green-400">Coach mode</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Sources button for mobile */}
            {(sources.length > 0 || openverseRecommendations.length > 0) && (
              <button
                onClick={() => setIsSourcesPanelOpen(true)}
                className="lg:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700/50 transition-colors relative"
                aria-label="View sources"
              >
                <BookOpenIcon className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full text-xs"></span>
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Main Content Area - Responsive Layout */}
      <div className="flex-1 overflow-hidden relative z-10">
        <div className="h-full flex">
          {/* Desktop: Chat History Sidebar - Add margin to ensure visibility */}
          {user && (
            <div className="hidden lg:block w-80 xl:w-96 flex-shrink-0 border-r border-gray-700/50 ml-4">
              <div className="h-full overflow-hidden">
                <ChatHistory
                  onLoadThread={loadChatThread}
                  onNewChat={startNewChat}
                  currentChatId={currentChatId}
                  isVisible={true}
                  refreshTrigger={chatHistoryRefreshTrigger}
                />
              </div>
            </div>
          )}

          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              <div className="max-w-4xl mx-auto">
                {messages.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-center space-y-8 py-8 lg:py-16"
                  >
                    <div className="space-y-4">
                      <h2 className="text-2xl sm:text-3xl font-bold text-white">
                        {user ? `Hello ${user.name || user.email?.split('@')[0] || 'there'}!` : 'What wisdom do you seek?'}
                      </h2>
                      <div className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
                        {user ? (
                          <div className="space-y-3">
                            <p>Greetings! I am Gandalf the Wise, your Open Org wizard. I have 2 modes:</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm">
                              <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full border border-primary/20">
                                <SearchIcon className="w-4 h-4 text-primary-light" />
                                <span className="text-primary-light font-medium">Search Mode</span>
                                <span className="text-gray-400">- Find resources instantly</span>
                              </div>
                              <span className="text-gray-500">or</span>
                              <div className="flex items-center gap-2 bg-green-500/10 px-3 py-1.5 rounded-full border border-green-500/20">
                                <BrainIcon className="w-4 h-4 text-green-400" />
                                <span className="text-green-400 font-medium">Coach Mode</span>
                                <span className="text-gray-400">- Get guided support</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p>Greetings! I am Gandalf the Wise, your Open Org wizard.</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm">
                              <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full border border-primary/20">
                                <SearchIcon className="w-4 h-4 text-primary-light" />
                                <span className="text-primary-light font-medium">Find resources</span>
                              </div>
                              <span className="text-gray-500">or</span>
                              <div className="flex items-center gap-2 bg-green-500/10 px-3 py-1.5 rounded-full border border-green-500/20">
                                <BrainIcon className="w-4 h-4 text-green-400" />
                                <span className="text-green-400 font-medium">Get coaching</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                          <span>Open Org Playbooks</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                          <span>Curated Openverse Resources</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          <span>Google for HR</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                      {(chatMode === 'search' ? [
                        "What playbooks and resources do you have available?",
                        "Find the best performance management tools",
                        "Show me onboarding EX inspiration",
                        "I'm struggling with team dynamics (try Coach Mode)"
                      ] : [
                        "I'm facing a team challenge, can you help?",
                        "I'm struggling to influence the executive team", 
                        "How do I improve our eNPS?",
                        "Find performance management resources (try Search Mode)"
                      ]).map((question, index) => {
                        // Check if this is a cross-mode suggestion
                        const hasCoachModeText = question.includes('(try Coach Mode)');
                        const hasSearchModeText = question.includes('(try Search Mode)');
                        
                        // Split the text for color coding
                        let questionParts = [];
                        if (hasCoachModeText) {
                          const parts = question.split('(try Coach Mode)');
                          questionParts = [
                            parts[0],
                            { text: '(try Coach Mode)', color: 'text-green-400' },
                            parts[1] || ''
                          ];
                        } else if (hasSearchModeText) {
                          const parts = question.split('(try Search Mode)');
                          questionParts = [
                            parts[0],
                            { text: '(try Search Mode)', color: 'text-indigo-400' },
                            parts[1] || ''
                          ];
                        } else {
                          questionParts = [question];
                        }
                        
                        return (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handlePromptClick(question)}
                            className="p-4 text-left bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                                                             <div className="flex-shrink-0">
                                 {chatMode === 'search' ? (
                                   <Wand2 className="h-5 w-5 text-indigo-500" />
                                 ) : (
                                   <Brain className="h-5 w-5 text-green-400" />
                                 )}
                               </div>
                              <span className="text-white group-hover:text-gray-200 leading-relaxed">
                                {questionParts.map((part, partIndex) => (
                                  <span key={partIndex} className={part.color || ''}>
                                    {part.text || part}
                                  </span>
                                ))}
                              </span>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : (
                  <div className="space-y-6 sm:space-y-8">
                    <AnimatePresence>
                      {messages.map((message) => (
                        <ChatMessage key={message.id} message={message} sourceRefs={sourceRefs} />
                      ))}
                    </AnimatePresence>

                    {/* Streaming Message */}
                    {isLoading && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        {isSearching && <SearchingIndicator mode={chatMode} />}
                        
                        {currentStreamingMessage && (
                          <ChatMessage
                            message={{
                              id: 'streaming',
                              content: currentStreamingMessage,
                              role: 'assistant',
                              timestamp: new Date(),
                              isStreaming: true,
                              sources: sources
                            }}
                            sourceRefs={sourceRefs}
                          />
                        )}
                      </motion.div>
                    )}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

                        {/* Input Area */}
            <div className="border-t border-gray-700/50 bg-gray-800/30 backdrop-blur-sm">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
                <form onSubmit={handleSubmit} className="flex items-end space-x-3">
                  {/* Mode Toggle with Icons + Text */}
                  <motion.div 
                    className="flex items-center bg-gray-700/50 rounded-lg p-1 border border-gray-600/30 mb-1"
                    animate={{
                      borderColor: chatMode === 'coach' ? 'rgb(34 197 94 / 0.3)' : 'rgb(107 114 128 / 0.3)'
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleModeChange('search')}
                      className={`px-3 py-2 rounded-md transition-all duration-300 flex items-center gap-2 text-sm font-medium ${
                        chatMode === 'search' 
                          ? 'bg-primary text-white shadow-md' 
                          : 'text-gray-400 hover:text-white hover:bg-gray-600/50'
                      }`}
                      title="Search Mode - Find resources immediately"
                    >
                      <SearchIcon className="w-4 h-4" />
                      <span>Search</span>
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleModeChange('coach')}
                      className={`px-3 py-2 rounded-md transition-all duration-300 flex items-center gap-2 text-sm font-medium ${
                        chatMode === 'coach' 
                          ? 'bg-green-500 text-white shadow-md shadow-green-500/25' 
                          : 'text-gray-400 hover:text-white hover:bg-gray-600/50'
                      }`}
                      title="Coach Mode - Ask clarifying questions first"
                    >
                      <BrainIcon className="w-4 h-4" />
                      <span>Coach</span>
                    </motion.button>
                  </motion.div>
                  
                  <div className="flex-1 relative">
                    <textarea
                      ref={inputRef}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        chatMode === 'coach' 
                          ? "Tell Gandalf your challenge & he'll help you work it out..."
                          : "Ask Gandalf to find resources, playbooks, and templates..."
                      }
                      disabled={isLoading}
                      rows={1}
                      className="w-full bg-gray-700/50 border border-gray-600/50 rounded-xl px-4 py-3 pr-12 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 disabled:opacity-50 backdrop-blur-sm text-sm sm:text-base resize-none overflow-hidden transition-all duration-200 ease-out"
                      style={{ minHeight: '48px' }}
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-gray-500 pointer-events-none">
                     
                    </div>
                  </div>
                  <motion.button
                    type="submit"
                    disabled={!inputValue.trim() || isLoading}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-primary hover:bg-primary-dark disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl px-4 sm:px-6 py-3 transition-all duration-200 flex items-center space-x-2 shadow-lg mb-1"
                  >
                    {isLoading ? (
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    ) : (
                      <PaperAirplaneIcon className="w-5 h-5" />
                    )}
                    <span className="hidden sm:inline">Ask</span>
                  </motion.button>
                </form>
              </div>
            </div>
          </div>

          {/* Desktop: Sources Sidebar */}
          {(sources.length > 0 || openverseRecommendations.length > 0 || currentStreamingMessage) && (
            <div className="hidden lg:block w-80 xl:w-96 flex-shrink-0 border-l border-gray-700/50">
              <div className="h-full overflow-y-auto p-4 space-y-4">
                {sources.length > 0 && (
                  <SourceCitations sources={sources} sourceRefs={sourceRefs} />
                )}
                
                {openverseRecommendations.length > 0 && (
                  <OpenverseRecommendations recommendations={openverseRecommendations} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: Chat History Panel */}
      <AnimatePresence>
        {isHistoryPanelOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsHistoryPanelOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-80 bg-gray-900/95 backdrop-blur-sm z-50 shadow-2xl"
            >
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                  <h2 className="text-lg font-semibold text-white">Thread History</h2>
                  <button
                    onClick={() => setIsHistoryPanelOpen(false)}
                    className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700/50 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-hidden">
                  <ChatHistory
                    onLoadThread={(id) => {
                      loadChatThread(id);
                      setIsHistoryPanelOpen(false);
                    }}
                    onNewChat={() => {
                      startNewChat();
                      setIsHistoryPanelOpen(false);
                    }}
                    currentChatId={currentChatId}
                    isVisible={true}
                    refreshTrigger={chatHistoryRefreshTrigger}
                  />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mobile: Sources Panel */}
      <AnimatePresence>
        {isSourcesPanelOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsSourcesPanelOpen(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm z-50 shadow-2xl rounded-t-2xl max-h-[80vh]"
            >
              <div className="flex flex-col max-h-full">
                <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                  <h2 className="text-lg font-semibold text-white">Sources & Resources</h2>
                  <button
                    onClick={() => setIsSourcesPanelOpen(false)}
                    className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700/50 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {sources.length > 0 && (
                    <SourceCitations sources={sources} sourceRefs={sourceRefs} />
                  )}
                  
                  {openverseRecommendations.length > 0 && (
                    <OpenverseRecommendations recommendations={openverseRecommendations} />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(5px) rotate(-1deg); }
        }
      `}</style>
    </div>
  );
};

export default ChatInterface; 